/**
 * /auth — unified passwordless entry point.
 *
 * State machine:
 *   'email'      — user enters their email address.
 *   'otp'        — user enters the 6-digit code we just emailed.
 *                  Backend decides at verify time whether the
 *                  outcome is 'authenticated' (session → home) or
 *                  'onboarding_required' (advance to onboarding).
 *   'onboarding' — brand-new email, collect first + last + phone
 *                  and complete the pending signup with the
 *                  onboardingToken issued at verify time.
 *   'completed'  — brief final state before the navigate() lands.
 *
 * No branching UI between "log in" vs "sign up" — every user starts
 * at the same email input. /login and /signup redirect here at the
 * route layer so the split is only historical.
 */

import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AuthLayout from '../../components/auth/AuthLayout.jsx';
import FormField from '../../components/auth/FormField.jsx';
import PhoneField from '../../components/ui/PhoneField/PhoneField.jsx';
import OtpInput from '../../components/Common/OtpInput.jsx';
import AuthButton from '../../components/auth/AuthButton.jsx';
import AuthNotice from '../../components/auth/AuthNotice.jsx';
import { safeInternalRedirect } from '../../components/auth/ProtectedRoute.jsx';
import { useAuth } from '../../context/AuthContext.jsx';
import { TERMS_URL, PRIVACY_URL } from '../../config/marketingSite.js';

// ── small helpers ──────────────────────────────────────────────────
function isValidEmail(v) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v || '').trim());
}
function maskEmail(email) {
  const [local, domain] = String(email || '').split('@');
  if (!domain) return email || '';
  return `${local.slice(0, 1)}…@${domain}`;
}

function useCountdown(untilTimestamp) {
  const [seconds, setSeconds] = useState(0);
  useEffect(() => {
    if (!untilTimestamp) { setSeconds(0); return undefined; }
    let handle = 0;
    const tick = () => {
      const left = Math.max(0, Math.ceil((untilTimestamp - Date.now()) / 1000));
      setSeconds(left);
      if (left === 0 && handle) clearInterval(handle);
    };
    tick();
    handle = setInterval(tick, 500);
    return () => clearInterval(handle);
  }, [untilTimestamp]);
  return seconds;
}

// ── page ───────────────────────────────────────────────────────────
function Auth() {
  const { requestOtp, verifyOtp, resendOtp, completeOnboarding } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [step, setStep] = useState('email');

  // Shared across steps.
  const [email, setEmail] = useState('');
  const [challengeId, setChallengeId] = useState(null);
  const [resendReadyAt, setResendReadyAt] = useState(0);
  const [onboardingToken, setOnboardingToken] = useState(null);

  // Step-local form state.
  const [otp, setOtp] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName,  setLastName]  = useState('');
  // Last report from the phone field: { e164, raw, valid, empty, message }.
  const [phone,     setPhone]     = useState(null);

  // UX bookkeeping.
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendNotice, setResendNotice] = useState(null);

  const cooldownLeft = useCountdown(resendReadyAt);

  // Guard against the button-click + auto-submit race where React hasn't
  // yet re-rendered the disabled state before a second submit lands.
  const inFlightRef = useRef(false);

  function goHome() {
    const params = new URLSearchParams(location.search);
    const redirect = safeInternalRedirect(params.get('redirect')) || '/';
    navigate(redirect, { replace: true });
  }

  // ── email → request OTP ───────────────────────────────────────
  async function submitEmail(e) {
    e.preventDefault();
    if (submitting) return;
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) { setErrors({ email: 'Email is required.' }); return; }
    if (!isValidEmail(trimmed)) {
      setErrors({ email: 'Enter a valid email address.' });
      return;
    }
    setErrors({}); setApiError(null); setSubmitting(true);
    try {
      const res = await requestOtp({ email: trimmed });
      setEmail(trimmed);
      setChallengeId(res.challengeId);
      setResendReadyAt(new Date(res.resendAvailableAt).getTime());
      setOtp('');
      setStep('otp');
    } catch (err) {
      setApiError(err?.response?.data?.message || 'We could not send a code. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  // ── OTP → verify (routes to authenticated or onboarding) ──────
  // `codeOverride` lets the OtpInput's onComplete hand us the fresh
  // value directly so we don't race the `otp` state update.
  async function submitOtp(eOrCode) {
    let codeOverride;
    if (typeof eOrCode === 'string') {
      codeOverride = eOrCode;
    } else if (eOrCode && typeof eOrCode.preventDefault === 'function') {
      eOrCode.preventDefault();
    }
    if (submitting || inFlightRef.current) return;
    const clean = String(codeOverride ?? otp).replace(/\D/g, '');
    if (clean.length !== 6) {
      setErrors({ otp: 'Please enter all 6 digits.' });
      return;
    }
    inFlightRef.current = true;
    setErrors({}); setApiError(null); setSubmitting(true);
    try {
      const res = await verifyOtp({ challengeId, otp: clean });
      if (res?.outcome === 'authenticated') {
        setStep('completed');
        goHome();
        return;
      }
      if (res?.outcome === 'onboarding_required') {
        setOnboardingToken(res.onboardingToken);
        setEmail(res.email || email);
        setStep('onboarding');
        return;
      }
      setApiError('Unexpected response — please try again.');
    } catch (err) {
      const code = err?.response?.data?.error;
      if (code === 'INVALID_OTP')          setApiError('That code is not correct.');
      else if (code === 'OTP_EXPIRED')     setApiError('This code has expired. Request a new one.');
      else if (code === 'TOO_MANY_ATTEMPTS') setApiError('Too many attempts. Request a new code.');
      else if (code === 'INVALID_CHALLENGE') setApiError('This session has expired. Please start again.');
      else if (code === 'ACCOUNT_BLOCKED')  setApiError('This account cannot sign in right now.');
      else setApiError(err?.response?.data?.message || 'Verification failed. Please try again.');
      setOtp('');
    } finally {
      inFlightRef.current = false;
      setSubmitting(false);
    }
  }

  // ── Onboarding → complete ─────────────────────────────────────
  async function submitOnboarding(e) {
    e.preventDefault();
    if (submitting) return;
    const next = {};
    if (!firstName.trim()) next.firstName = 'First name is required.';
    if (!lastName.trim())  next.lastName  = 'Last name is required.';
    if (!phone || phone.empty) next.phone = 'Mobile number is required.';
    else if (!phone.valid)     next.phone = phone.message;
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    setApiError(null); setSubmitting(true);
    try {
      await completeOnboarding({
        onboardingToken,
        firstName: firstName.trim(),
        lastName:  lastName.trim(),
        phone:     phone.e164,
      });
      setStep('completed');
      goHome();
    } catch (err) {
      const code = err?.response?.data?.error;
      if (code === 'ONBOARDING_TOKEN_EXPIRED') {
        setApiError('Your onboarding session has expired. Please start again.');
      } else if (code === 'INVALID_ONBOARDING_TOKEN') {
        setApiError('This onboarding session is no longer valid. Please start again.');
      } else if (code === 'ACCOUNT_BLOCKED') {
        setApiError('This account cannot sign in right now.');
      } else {
        setApiError(err?.response?.data?.message || 'We could not create your account. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  // ── Back / change-email → return to step 1 ───────────────────
  function changeEmail() {
    setStep('email');
    setChallengeId(null);
    setOnboardingToken(null);
    setOtp('');
    setResendReadyAt(0);
    setResendNotice(null);
    setApiError(null);
    setErrors({});
  }

  // ── Resend ───────────────────────────────────────────────────
  async function onResend() {
    if (resending || cooldownLeft > 0 || !challengeId) return;
    setResending(true); setResendNotice(null); setApiError(null);
    try {
      const res = await resendOtp({ challengeId });
      setResendReadyAt(new Date(res.resendAvailableAt).getTime());
      setResendNotice('If the address can receive email, a new code has been sent.');
      setOtp('');
    } catch (err) {
      const code = err?.response?.data?.error;
      if (code === 'COOLDOWN_ACTIVE' && err?.response?.data?.resendAvailableAt) {
        setResendReadyAt(new Date(err.response.data.resendAvailableAt).getTime());
      } else if (code === 'INVALID_CHALLENGE') {
        setApiError('This session has expired. Please start again.');
      } else {
        setApiError('Could not resend the code. Please try again shortly.');
      }
    } finally {
      setResending(false);
    }
  }

  // ── Render ───────────────────────────────────────────────────
  if (step === 'email') {
    return (
      <AuthLayout
        title="What’s your email address?"
        subtitle="We’ll send you a verification code."
      >
        <form onSubmit={submitEmail} noValidate>
          <div className="auth-body" style={{ gap: 20 }}>
            <FormField
              label="Email address" name="email" type="email"
              value={email} onChange={(e) => setEmail(e.target.value)}
              error={errors.email} autoComplete="email" inputMode="email"
              placeholder="you@example.com" required autoFocus
            />
            {apiError && <AuthNotice variant="error">{apiError}</AuthNotice>}
            <AuthButton busy={submitting}>Continue</AuthButton>
            <p className="auth-fine-print">
              By continuing, you agree to the{' '}
              {/* Real cross-origin anchors — not react-router <Link> —
                  because the marketing site is a separate app on a
                  different subdomain. Same-tab navigation so the
                  browser Back button naturally returns to /auth. */}
              <a
                className="auth-legal-link"
                href={TERMS_URL}
                rel="noopener"
              >
                Terms of Service
              </a>{' '}
              and{' '}
              <a
                className="auth-legal-link"
                href={PRIVACY_URL}
                rel="noopener"
              >
                Privacy Policy
              </a>
              .
            </p>
          </div>
        </form>
      </AuthLayout>
    );
  }

  if (step === 'otp') {
    return (
      <AuthLayout
        title="Enter your verification code"
        subtitle={`Enter the 6-digit code sent to ${maskEmail(email)}.`}
        back={{ onClick: changeEmail, label: 'Use a different email address' }}
      >
        <form onSubmit={submitOtp} noValidate>
          <div className="auth-body" style={{ gap: 20 }}>
            <OtpInput
              label="6-digit code"
              value={otp}
              onChange={setOtp}
              onComplete={submitOtp}
              disabled={submitting}
              error={errors.otp}
              autoFocus
            />
            {apiError && <AuthNotice variant="error">{apiError}</AuthNotice>}
            {resendNotice && <AuthNotice variant="success">{resendNotice}</AuthNotice>}
            <AuthButton busy={submitting}>Continue</AuthButton>
            <div className="auth-actions">
              <button
                type="button"
                className="auth-inline-link"
                onClick={onResend}
                disabled={resending || cooldownLeft > 0}
              >
                {cooldownLeft > 0 ? `Resend in ${cooldownLeft}s` : 'Resend code'}
              </button>
            </div>
          </div>
        </form>
      </AuthLayout>
    );
  }

  if (step === 'onboarding') {
    return (
      <AuthLayout
        title="Tell us about yourself"
        subtitle="Just a few details and you're in."
      >
        <form onSubmit={submitOnboarding} noValidate>
          <div className="auth-body" style={{ gap: 20 }}>
            {/* Verified email displayed as locked — the user cannot
                edit it here; they'd have to restart the OTP flow. */}
            <FormField
              label="Email address" name="onboarding-email" type="email"
              value={email} onChange={() => { /* readonly */ }}
              readOnly aria-readonly="true"
              autoComplete="email"
              style={{ opacity: 0.75 }}
            />
            <FormField
              label="First name" name="firstName"
              value={firstName} onChange={(e) => setFirstName(e.target.value)}
              error={errors.firstName}
              autoComplete="given-name" required autoFocus
            />
            <FormField
              label="Last name" name="lastName"
              value={lastName} onChange={(e) => setLastName(e.target.value)}
              error={errors.lastName}
              autoComplete="family-name" required
            />
            <PhoneField
              label="Mobile number" name="phone"
              onChange={setPhone}
              error={errors.phone}
              required
            />
            {apiError && <AuthNotice variant="error">{apiError}</AuthNotice>}
            <AuthButton busy={submitting}>Create account</AuthButton>
          </div>
        </form>
      </AuthLayout>
    );
  }

  // step === 'completed'
  return (
    <AuthLayout title="Signing you in…">
      <AuthNotice variant="success">You're all set. Redirecting…</AuthNotice>
    </AuthLayout>
  );
}

export default Auth;
